import { Box, Button, FormControl, FormLabel, Grid, Stack, TextField, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { DataGrid } from "@mui/x-data-grid";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { get } from "lodash";
import { default as Moment, default as moment } from 'moment';
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import branch from "../../services/branch";
import { rupiah } from "../../utils/currency";
import initial from "../../utils/initial";
import queryDefault from "../../utils/queryDefault";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function BranchDetail() {
  const [pageSize, setPageSize] = useState<number>(10);
  const location = useLocation();
  const path = location.pathname.split("/");
  const branch_id = path.pop() || "";
  const [valueFrom, setValueFrom] = useState<Dayjs | null>(dayjs(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [valueTo, setValueTo] = useState<Dayjs | null>(dayjs(new Date()));
  const [countTotal, setCountTotal] = useState(0);
  const [countSales, setCountSales] = useState(0);

  const [options, setOptions] = useState<any>({
    branch_id: branch_id,
    startDate: Moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('YYYY-MM-DD'),
    endDate: Moment(new Date()).format('YYYY-MM-DD'),
  });

  const form = useForm({
    defaultValues: options,
  });

  const [data, setData] = useState({
    id: branch_id,
    name: "",
    place: "",
    email: "",
    priceperunit: 0,
    issplitrevenue: false,
    splitrevenuepercentage: 0,
    isqrauto: false,
    mdr: 0,
    isppn: false,
    ppn: 0,
    minsplitrevenue: 0,
    reference_xendit: "",
    reference_midtrans: "",
    bank_rekening: "",
    no_rekening: "",
    atas_nama: "",
    npwp: "",
    group: "",
    npwp_image: initial.IMAGE_NPWP,
    ktp: "",
    nama_ktp: "",
  });

  const [countStats, setCountStats] = useState([
    { grouped_date: "", total_kertas: 0 },
  ]);

  const [salesStats, setSalesStats] = useState([
    { grouped_date: "", subtotal: 0 },
  ]);

  const chartSetting = {
    height: 300,
  };

  const listSplitRevenue = useQuery(
    ["revenue", options],
    ({ queryKey }) => {
      return branch.getAllSplitRevenue({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  const listBranchStats = useQuery(
    ["stats", options],
    ({ queryKey }) => {
      return branch.getBranchStatsRange({
        ...queryKey[1],
      });
    },
    {
      ...queryDefault,
    }
  );

  useEffect(() => {
    branch.getDetailBranch(branch_id).then((response) => {
      setData({
        id: branch_id,
        name: response.data.name,
        place: response.data.place,
        email: response.data.email,
        priceperunit: response.data.priceperunit,
        issplitrevenue: response.data.issplitrevenue,
        splitrevenuepercentage: response.data.splitrevenuepercentage,
        isqrauto: response.data.isqrauto,
        mdr: response.data.mdr,
        isppn: response.data.isppn,
        ppn: response.data.ppn,
        minsplitrevenue: response.data.minsplitrevenue,
        reference_xendit: response.data.reference_xendit,
        reference_midtrans: response.data.reference_midtrans,
        bank_rekening: response.data.bank_rekening,
        no_rekening: response.data.no_rekening,
        atas_nama: response.data.atas_nama,
        npwp: response.data.npwp,
        group: response.data.group_name,
        npwp_image: !response.data.npwp_image || response.data.npwp_image === ""
          ? initial.IMAGE_NPWP
          : "data:image/png;base64," + response.data.npwp_image,
        ktp: response.data.ktp,
        nama_ktp: response.data.nama_ktp,
      });
    });
  }, [branch_id]);

  useEffect(() => {
    if (listBranchStats.data?.data?.count?.length > 0) {
      var countStats: any = [];
      var countTotalTemp = 0;
      for (const i of listBranchStats.data.data.count) {
        countStats.push({
          grouped_date: `${i.grouped_date.split('-')[2]}-${i.grouped_date.split('-')[1]}`,
          total_kertas: Number(i.total_kertas)
        });
        countTotalTemp = countTotalTemp + Number(i.total_kertas);
      }
      setCountStats(countStats);
      setCountTotal(countTotalTemp);
    } else {
      setCountStats([]);
      setCountTotal(0);
    }
    if (listBranchStats.data?.data?.sales?.length > 0) {
      var salesStats: any = [];
      var countSalesTemp = 0;
      for (const i of listBranchStats.data.data.sales) {
        salesStats.push({
          grouped_date: `${i.grouped_date.split('-')[2]}-${i.grouped_date.split('-')[1]}`,
          subtotal: Number(i.subtotal)
        });
        countSalesTemp = countSalesTemp + Number(i.subtotal);
      }
      setSalesStats(salesStats);
      setCountSales(countSalesTemp)
    } else {
      setSalesStats([]);
      setCountSales(0);
    }
  }, [listBranchStats]);

  useEffect(() => {
    form.setValue("startDate", Moment(valueFrom?.format('YYYY-MM-DD')).format('YYYY-MM-DD'));
    form.setValue("endDate", Moment(valueTo?.format('YYYY-MM-DD')).format('YYYY-MM-DD'));
    setOptions({ ...options, startDate: form.getValues().startDate, endDate: form.getValues().endDate });
  }, [valueFrom, valueTo, form]);

  const notification = useNotification();
  const alert = useAlert();
  const deleteSales = useMutation("delete-sales", branch.deleteSplitRevenue, {
    onSuccess: () => {
      listSplitRevenue.refetch();
      notification.onOpen({
        message: "Split Revenue was successfully deleted!",
        type: "success",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onDelete = (id: number) => {
    alert.onClose();
    deleteSales.mutate(id);
  };

  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete Split Revenue?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  const navigate = useNavigate();
  const handleOnCellClick = (id: number) => {
    navigate(`/app/branch/split-revenue/${id}`);
  };

  const uploadNPWP = useMutation("upload_template", branch.uploadNPWP, {
    onSuccess: () => window.location.reload()
  });

  const handleUploadNPWP = (event: React.ChangeEvent<HTMLInputElement>) => {
    const formData = new FormData();
    formData.append("id", data.id);
    event.target.files && formData.append("file", event.target.files[0]);
    uploadNPWP.mutate(formData);
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Branch Detail</title>
      </Helmet>

      <Breadcrumb
        breadcrumbs={[{ label: "Branch", href: "/app/branch" }]}
        label={data.name}
        href={`/app/branch/`}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={{ lg: 4, md: 2 }}>
            <Grid item lg={4} md={12}>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Name
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.name}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Group
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.group}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Place
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.place}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Email
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.email}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Price Per Unit
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.priceperunit}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Split Revenue?
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.issplitrevenue ? "Yes" : "No"}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Split Revenue %
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.splitrevenuepercentage}
                </Typography>
              </Stack>
            </Grid>
            <Grid item lg={4} md={12}>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Minimal Split Revenue
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.minsplitrevenue}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  QR Type
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.isqrauto ? "Auto" : "Manual"}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  MDR %
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.mdr}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Use PPN?
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.isppn ? "Yes" : "No"}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  PPN %
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.ppn}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Reference for Xendit Report
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.reference_xendit}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Reference for Midtrans Report
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.reference_midtrans}
                </Typography>
              </Stack>
            </Grid>
            <Grid item lg={4} md={12}>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Rekening
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {`${data.bank_rekening} ${data.no_rekening} a/n ${data.atas_nama}`}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  NPWP
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3, mb: 2 }}
                >
                  {data.npwp}
                </Typography>
                <img src={data.npwp_image} alt="npwp" width="100%" />
                <Button variant="contained" component="label" sx={{ mt: 2 }}>
                  Upload NPWP <input onChange={handleUploadNPWP} type="file" hidden />
                </Button>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  KTP
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.ktp}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Nama Sesuai KTP
                </Typography>
                <Typography
                  variant="body1"
                  color="green"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.nama_ktp}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h2">Stats</Typography>

          <Box my={2}>
            <Stack direction="row" spacing={1}>
              <FormControl margin="normal">
                <FormLabel>Start Date</FormLabel>
                <Controller
                  name="date_from"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        inputFormat="DD/MM/YYYY"
                        views={["day", "month", "year"]}
                        value={valueFrom}
                        onChange={newValue => {
                          setValueFrom(newValue);
                        }}
                        renderInput={props => <TextField {...props} />}
                      />
                    </LocalizationProvider>
                  )}
                />
              </FormControl>

              <FormControl margin="normal">
                <FormLabel>End Date</FormLabel>
                <Controller
                  name="date_to"
                  control={form.control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        inputFormat="DD/MM/YYYY"
                        views={["day", "month", "year"]}
                        value={valueTo}
                        onChange={newValue => {
                          setValueTo(newValue);
                        }}
                        renderInput={props => <TextField {...props} />}
                      />
                    </LocalizationProvider>
                  )}
                />
              </FormControl>
            </Stack>
          </Box>
          <Typography variant="h3" sx={{ mt: 2 }}>Total Kertas ({countTotal})</Typography>
          <BarChart
            sx={{ "& .MuiChartsAxis-tickLabel tspan": { fontSize: "10px" } }}
            dataset={countStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "grouped_date",
                label: "Date-Month",
              },
            ]}
            series={[{ dataKey: "total_kertas", label: "Total" }]}
            {...chartSetting}
          />
          <Typography variant="h3" sx={{ mt: 2 }}>Revenue ({rupiah(countSales)})</Typography>
          <BarChart
            sx={{ "& .MuiChartsAxis-tickLabel tspan": { fontSize: "10px" } }}
            dataset={salesStats}
            xAxis={[
              {
                scaleType: "band",
                dataKey: "grouped_date",
                label: "Date-Month",
              },
            ]}
            series={[{ dataKey: "subtotal", label: "Total" }]}
            {...chartSetting}
          />
        </Box>

        <Typography sx={{
          fontWeight: 'bold',
          mt: 2
        }}>Split Revenue History</Typography>
        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={listSplitRevenue.isLoading}
            getRowId={(record) => get(record, "id")}
            rows={get(listSplitRevenue, "data.data", [])}
            rowCount={get(listSplitRevenue, "data.data", []).length}
            page={options.page_number}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}

            columns={[
              {
                field: "percentage",
                headerName: "Split Revenue Percentage",
                flex: 1,
                filterable: false,
                minWidth: 150,
              },
              {
                field: "effectivedate",
                headerName: "Effective Date",
                flex: 1,
                filterable: false,
                minWidth: 150,
                renderCell: (params) => {
                  if (!params.value) {
                    return "-";
                  }
                  return moment(params.value).format("DD/MM/YYYY");
                },
              },
              {
                field: "actions",
                headerName: "Action",
                sortable: false,
                filterable: false,
                width: 200,
                renderCell: (params) => {
                  return (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        onClick={() => handleOnCellClick(params.row.id)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => onClickDelete(params.row.id)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  );
                },
              },
            ]}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}
